import React, { useEffect, useState } from "react";
import {
    IResourceComponentsProps,
    BaseRecord,
    useTranslate,
    useGetIdentity,
    useExport,
    CrudFilters,
    CrudFilter,
    useNotification,
} from "@refinedev/core";
import {
    useTable,
    List,
    EditButton,
    ShowButton,
    DateField,
    TextField,
    ExportButton,
    useSelect,
    getDefaultSortOrder,
    FilterDropdown,
    DeleteButton,
} from "@refinedev/antd";
import { Table, Space, Tag, Button, Breadcrumb, Form, Input, DatePicker, FormProps, Card, Select, message, Row, Col, Typography, Switch, Divider, Pagination, Flex, Tooltip, Grid } from "antd";
import { dateSorter, flattenData } from "../../shared/utils";
import { CarMakeLabel, GlassCard } from "../../shared/components";
import { StatusLabels } from "./components/statusLabels";
import { UserRole } from "../../models/role";
import {
    MessageTwoTone,
    CalendarOutlined,
    ClockCircleOutlined,
    TableOutlined,
    AppstoreOutlined,
    ContainerOutlined,
    CarOutlined,
    CodeOutlined,
    IdcardOutlined,
    TagsOutlined,
    SettingOutlined,
    EyeOutlined,
    BarcodeOutlined,
} from "@ant-design/icons";
import { CustomerBackButton } from "./components/customerBackButton";
import { CustomerBreadcrumb } from "./components/customerBreadcrumb";
import dayjs from 'dayjs';
import { useAdmin } from "../../hooks/auth";
import useOrdersExport from "./hooks";
import { ListFilter } from "./components/listFilter";
import { BuildingOfficeIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { theme } from "antd";
import { OrderCard } from "./components/OrderCard";

const { useToken } = theme;

interface IUser {
    id: number;
    name: string;
    avatar: string;
    role: {
        id: UserRole;
    };
    account: {
        id: number;
        name: string;
    };
}

export const SavList: React.FC<IResourceComponentsProps> = () => {
    const _t = useTranslate();
    const { data: user } = useGetIdentity<IUser>();
    const isAdmin = useAdmin();
    const account = user?.account;
    const navigate = useNavigate();
    const [orders, setOrders] = useState([] as BaseRecord[]);
    const [fieldLabels, setFieldLabels] = useState({} as any);
    const [globalFilters, setGlobalFilters] = useState([] as CrudFilter[]);
    const [exportQueryParams, setExportQueryParams] = useState("");

    // Initialize isCardView from localStorage, default to false (table view)
    const [isCardView, setIsCardView] = useState(() => {
        const saved = localStorage.getItem('orders-sav-view-preference');
        return saved ? JSON.parse(saved) : false;
    });

    const { open } = useNotification();
    const [messageApi, contextHolder] = message.useMessage();
    const { token } = useToken();
    const breakpoint = Grid.useBreakpoint();
    const isMobile = typeof breakpoint.lg === "undefined" ? false : !breakpoint.lg;
    const effectiveCardView = isMobile || isCardView;

    // Save view preference to localStorage whenever it changes
    const handleViewChange = (checked: boolean) => {
        setIsCardView(checked);
        localStorage.setItem('orders-sav-view-preference', JSON.stringify(checked));
    };

    // Handle card click to navigate to order show page
    const handleCardClick = (order: any) => {
        if (user?.role?.id === UserRole.admin) {
            navigate(`/orders/show/${order.id}`);
        } else if (user?.role?.id === UserRole.user && account) {
            navigate(`/accounts/${account.id}/orders/show/${order.id}`);
        }
    };

    const { tableProps, tableQueryResult, searchFormProps, sorters, current, setCurrent, pageSize, setPageSize } = useTable({
        resource: "orders",
        syncWithLocation: true,
        sorters: {
            mode: "server",
            initial: [
                {
                    field: "id",
                    order: "desc",
                },
                {
                    field: "createdAt",
                    order: "desc",
                }
            ],
        },
        filters: {
            initial: [
                {
                    field: "status",
                    operator: "eq",
                    value: "sav",
                }
            ]
        },
        pagination: {
            mode: "server",
        },
        onSearch: (params: Record<string, any>) => {
            const filters: CrudFilters = [];
            const { q, vin, billingMode, status, account, createdAt } = params;
            console.log("Search params", params);

            filters.push(
                {
                    field: "account",
                    operator: "eq",
                    value: account,
                },
                {
                    field: "status",
                    operator: "eq",
                    value: status,
                },
                {
                    field: "vin",
                    operator: "eq",
                    value: vin,
                },
                {
                    field: "billingMode",
                    operator: "eq",
                    value: billingMode,
                },
                {
                    field: "createdAt",
                    operator: "gte",
                    value: createdAt ? createdAt[0].format("YYYY-MM-DD") : undefined,
                },
                {
                    field: "createdAt",
                    operator: "lte",
                    value: createdAt ? createdAt[1].format("YYYY-MM-DD") : undefined,
                },
            );

            setGlobalFilters(filters);
            console.log("Filters", filters);

            return filters;
        },
    });



    useEffect(() => {
        console.log("Global filters", globalFilters);
        // Construct query params based on global filters to be used for export
        // Query looks like `?createdAt_gte=2024-04-26&createdAt_lte=2024-05-23&status=completed`;
        const queryParams = globalFilters
            .filter((filter: any) => filter.value)
            .map((filter: any) => {
                if (filter.field === "createdAt") {
                    return `${encodeURIComponent(filter.field)}_${filter.operator}=${encodeURIComponent(filter.value)}`;
                }
                return `${encodeURIComponent(filter.field)}=${encodeURIComponent(filter.value)}`;
            })
            .join("&");
        console.log("Query params", queryParams);
        setExportQueryParams(queryParams);
    }, [globalFilters]);

    useEffect(() => {
        if (isAdmin) {
            const intervalId = setInterval(() => {
                messageApi.open({
                    type: 'loading',
                    content: 'Action in progress..',
                    duration: 100,
                });
                tableQueryResult.refetch();
            }, 10000); // Fetches every 30 seconds

            // Clear interval on component unmount
            return () => clearInterval(intervalId);
        }
    }, [isAdmin]); // Empty dependency array means this effect runs once on mount and clean up on unmount

    // Close message on successful data refresh
    tableQueryResult.isSuccess && messageApi.destroy();

    const { triggerExport: triggerExport, isLoading: exportLoading } = useOrdersExport({
        queryParams: exportQueryParams,
        mapData: (item: any) => {
            const order = mapOrders([item])[0];
            console.log("Item", order);
            const products = order.products
                .map((product: any) => product.name ?? "")
                .join(", ");
            console.log("Products", products);

            return {
                id: order.id,
                date: dayjs(order.createdAt).format("DD/MM/YYYY HH:mm"),
                marque: order.make,
                modele: order.model,
                vin: order.vin,
                produits: products,
                compte: order.account?.name ?? "",
            };
        }
    });

    const mapOrders = (data: any) => {
        return data.map((order: any) => {
            //console.log("Order data", order);
            let formFieldValues: any = {};
            if (order.payload !== undefined && order.payload !== null && order.payload.trim() !== '') {
                //console.log("Order payload", order.payload)
                const formData = flattenData(JSON.parse(order.payload));
                //console.log("Flattend data", formData);
                for (let i = 0; i < formData.length; i++) {
                    if (formData[i].value !== undefined)
                        formFieldValues[formData[i].name] = formData[i].value;
                }
                const labels = formData.reduce((acc: any, item: any) => {
                    acc[item.name] = item.label;
                    return acc;
                });
                setFieldLabels(labels);
                //console.log(formFieldValues);
            }
            const result = {
                ...order,
                ...formFieldValues
            };
            //console.log(result);
            return result;
        });
    }

    useEffect(() => {
        if (tableQueryResult?.data?.data) {
            const data = tableQueryResult?.data?.data;
            let os = mapOrders(data);
            os.sort((a: any, b: any) => (b.unreadMessages?.length || 0) - (a.unreadMessages?.length || 0));
            setOrders(os);
            console.log("Setting Orders", os);
        }
    }, [tableQueryResult.data]);

    const renderCardView = () => (
        <div>
            <Card
                className={isMobile ? "shadow-none" : "shadow-lg"}
                style={isMobile ? { background: "transparent", border: "none" } : undefined}
                bodyStyle={{ padding: "8px 0 8px 0" }}
            >
                <div className={isMobile ? "px-3 pt-4" : "px-8 pt-6"}>
                    <Flex className="text-xl mb-4">
                        {user?.role?.id == UserRole.user && (
                            <CustomerBackButton />
                        )}
                        <ContainerOutlined className="mr-2" style={{ color: token.colorPrimary }} />
                        {_t("orders.titles.savList")}
                    </Flex>
                </div>

                {/* Header with column labels */}
                <div className="px-8 py-4" style={{ display: isMobile ? "none" : undefined, borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
                    <div className="grid gap-4 text-sm font-medium" style={{
                        gridTemplateColumns: '80px 120px 1fr 1fr 1fr 1fr 120px',
                        color: token.colorTextSecondary
                    }}>
                        <div className="flex items-center gap-2">
                            <BarcodeOutlined className="text-base" style={{ color: token.colorPrimary }} />
                            <span>{_t("orders.fields.id")}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <ClockCircleOutlined className="text-base" style={{ color: token.colorPrimary }} />
                            <span>{_t("orders.fields.createdAt")}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <CarOutlined className="text-base" style={{ color: token.colorPrimary }} />
                            <span>{fieldLabels["make"]} / {fieldLabels["model"]}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <CodeOutlined className="text-base" style={{ color: token.colorPrimary }} />
                            <span>{fieldLabels["vin"]}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <AppstoreOutlined className="text-base" style={{ color: token.colorPrimary }} />
                            <span>{_t("orders.fields.products")}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <IdcardOutlined className="text-base" style={{ color: token.colorPrimary }} />
                            <span>{_t("orders.fields.account")}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <TagsOutlined className="text-base" style={{ color: token.colorPrimary }} />
                            <span>{_t("orders.fields.status")}</span>
                        </div>
                    </div>
                </div>

                {orders.map((order: any) => (
                    <OrderCard
                        key={order.id}
                        order={order}
                        fieldLabels={fieldLabels}
                        onClick={handleCardClick}
                    />
                ))}
            </Card>

            {/* Pagination for card view */}
            {tableProps.pagination && (
                <div className="flex justify-center mt-4">
                    <Pagination
                        current={current}
                        pageSize={pageSize}
                        total={tableProps.pagination.total}
                        showSizeChanger={tableProps.pagination.showSizeChanger}
                        showQuickJumper={tableProps.pagination.showQuickJumper}
                        showTotal={tableProps.pagination.showTotal}
                        onChange={(page, pageSize) => {
                            setCurrent(page);
                            if (pageSize) {
                                setPageSize(pageSize);
                            }
                        }}
                        onShowSizeChange={(current, size) => {
                            setCurrent(current);
                            setPageSize(size);
                        }}
                    />
                </div>
            )}
        </div>
    );

    const renderTableView = () => (
        <GlassCard className="orders-list-table-view !p-0 overflow-hidden">
            <div className="px-8 pt-6">
                <Flex className="text-xl mb-4">
                    {user?.role?.id == UserRole.user && (
                        <CustomerBackButton />
                    )}
                    <ContainerOutlined className="mr-2" style={{ color: token.colorPrimary }} />
                    {_t("orders.titles.list")}
                </Flex>
            </div>
            <Table {...tableProps} rowKey="id" dataSource={orders} className="px-6">
                <Table.Column
                    dataIndex="id"
                    title={
                        <>
                            <BarcodeOutlined
                                style={{ marginRight: 8, color: token.colorPrimary }}
                            />{" "}
                            {_t("orders.fields.id")}
                        </>
                    }
                    width={90}
                />
                <Table.Column
                    dataIndex="createdAt"
                    title={
                        <>
                            <CalendarOutlined
                                style={{ marginRight: 8, color: token.colorPrimary }}
                            />{" "}
                            {_t("orders.fields.createdAt")}
                        </>
                    }
                    render={(value: any) =>
                        <Tag
                            style={{
                                backgroundColor: token.colorBgElevated,
                                color: token.colorText,
                                borderColor: token.colorBorderBg
                            }}
                        >
                            <DateField
                                className="text-xs"
                                value={value}
                                format={"DD MMM HH:mm"}
                                style={{ color: token.colorPrimary }}
                            />
                        </Tag>
                    }
                    width={180}
                //defaultSortOrder={getDefaultSortOrder("createdAt", sorters)}
                //sorter={{ multiple: 2 }}
                />
                <Table.Column
                    dataIndex="make"
                    title={
                        <>
                            <CarOutlined
                                style={{ marginRight: 8, color: token.colorPrimary }}
                            />{" "}
                            {fieldLabels["make"]}
                        </>
                    }
                    render={(value: string) => <CarMakeLabel make={value} />}
                />
                <Table.Column
                    dataIndex="model"
                    title={
                        <>
                            <CarOutlined
                                style={{ marginRight: 8, color: token.colorPrimary }}
                            />{" "}
                            {fieldLabels["model"]}
                        </>
                    }
                />
                <Table.Column
                    dataIndex="vin"
                    title={
                        <>
                            <CodeOutlined
                                style={{ marginRight: 8, color: token.colorPrimary }}
                            />{" "}
                            {fieldLabels["vin"]}
                        </>
                    }
                />
                <Table.Column
                    dataIndex="products"
                    title={
                        <>
                            <AppstoreOutlined
                                style={{ marginRight: 8, color: token.colorPrimary }}
                            />{" "}
                            {_t("orders.fields.products")}
                        </>
                    }
                    render={(value: any) => {
                        const fullText = value?.map((product: any) => product.name).join(", ") || "";
                        const truncatedText = fullText.length > 20 ? `${fullText.slice(0, 20)}...` : fullText;
                        return fullText.length > 20 ? (
                            <Tooltip title={fullText}>
                                <span>{truncatedText}</span>
                            </Tooltip>
                        ) : (
                            <span>{fullText}</span>
                        );
                    }}
                />
                <Table.Column
                    dataIndex={["account", "name"]}
                    title={
                        <>
                            <IdcardOutlined
                                style={{ marginRight: 8, color: token.colorPrimary }}
                            />{" "}
                            {_t("orders.fields.account")}
                        </>
                    }
                />
                <Table.Column
                    dataIndex="status"
                    title={
                        <>
                            <TagsOutlined
                                style={{ marginRight: 8, color: token.colorPrimary }}
                            />{" "}
                            {_t("orders.fields.status")}
                        </>
                    }
                    render={(value: any, record: any) => (
                        <Flex align="center">
                            {record?.unreadMessages?.length > 0 && (
                                <Tag color="processing" className="font-bold mr-2">
                                    <Flex align="center" gap={"small"}>
                                        <MessageTwoTone />
                                        {record.unreadMessages.length}
                                    </Flex>
                                </Tag>
                            )}
                            <StatusLabels value={value} />
                        </Flex>
                    )}
                    width={100}
                />
                <Table.Column
                    title={
                        <>
                            <SettingOutlined
                                style={{ marginRight: 8, color: token.colorPrimary }}
                            />{" "}
                            {_t("table.actions")}
                        </>
                    }
                    dataIndex="actions"
                    render={(_, record: BaseRecord) => (
                        <Space>
                            <EditButton
                                resource="orders"
                                hideText
                                size="small"
                                recordItemId={record.id}
                                hidden
                            />
                            {user?.role?.id === UserRole.admin && (
                                <>
                                    <ShowButton
                                        resource="orders"
                                        size="small"
                                        recordItemId={record.id}
                                    />
                                    {!(["canceled"].includes(record.status)) && (
                                        <DeleteButton
                                            resource="orders"
                                            size="small"
                                            recordItemId={record.id}
                                            onSuccess={() => { tableQueryResult.refetch() }}
                                        >
                                            {_t("buttons.cancel")}
                                        </DeleteButton>
                                    )}
                                </>
                            )}
                            {user?.role?.id === UserRole.user && account && (
                                <Button
                                    icon={<EyeOutlined />}
                                    size="small"
                                    href={`/accounts/${account.id}/orders/show/${record.id}`}
                                >
                                    {_t("buttons.show")}
                                </Button>
                            )}
                        </Space>
                    )}
                />
            </Table>
        </GlassCard>
    );

    return (
        <>
            <style>{`
                .orders-list-table-view .ant-table { background: transparent !important; }
                .orders-list-table-view .ant-table-thead > tr > th { background: transparent !important; }
                .orders-list-table-view .ant-table-tbody > tr > td { background: transparent !important; }
            `}</style>
            {contextHolder}
            <List
                canCreate={false}
                title={<>

                </>}
                breadcrumb={
                    <>

                    </>
                }
                resource="orders"
                headerButtons={
                    <Space>
                        {!isMobile && (
                            <div className="flex items-center space-x-2">
                                <TableOutlined
                                    style={{
                                        color: !isCardView ? token.colorPrimary : token.colorTextTertiary,
                                    }}
                                />
                                <Switch
                                    checked={isCardView}
                                    onChange={handleViewChange}
                                    checkedChildren={<AppstoreOutlined />}
                                    unCheckedChildren={<TableOutlined />}
                                />
                                <AppstoreOutlined
                                    style={{
                                        color: isCardView ? token.colorPrimary : token.colorTextTertiary,
                                    }}
                                />
                            </div>
                        )}
                        {isAdmin && (
                            <ExportButton onClick={triggerExport} loading={exportLoading} />
                        )}
                    </Space>
                }
            >
                {effectiveCardView ? renderCardView() : renderTableView()}
            </List>
        </>
    );
};

interface IAccount {
    id: number;
    name: string;
}

